import React from 'react';
import {RiSearchLine} from 'react-icons/ri';
import {useDispatch, useSelector} from 'react-redux';
import CustomDropdown from '~/components/dropdowns/CustomDropdown';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {
  BIRTH_CONTROL_TELECONSULTATION_KEYS,
  NO_OPTION,
  OTHER_BRAND,
  YES_OPTION,
} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer, setSelectedBrand} from '~/redux/questionnaires/slice';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_TELECONSULTATION_KEYS.BRAND_USED]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const selectedBrand = useSelector((state: RootState) => state.questionnaires.selectedBrand);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_TELECONSULTATION_KEYS.BRAND_USED);

  const rules = {
    medicationsTaking: (attributes: any) => {
      if (inputs.selectedHappyOption === NO_OPTION) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            tooShort: '^This field is too short (minimum is 5 characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
    otherBrandUsed: (attributes: any) => {
      if (inputs.brandUsed === OTHER_BRAND) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            tooShort: '^This field is too short (minimum is 5 characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
  };

  const {inputs, setInputs, validation, trimSpaces, trimSpacesOnChange} = useInput(
    {
      brandUsed: currentAnswer?.brandUsed || '',
      selectedOption: currentAnswer?.selectedOption || '',
      brandLabel: currentAnswer?.brandLabel,
      selectedHappyOption: currentAnswer?.selectedHappyOption || '',
      medicationsTaking: currentAnswer?.medicationsTaking || '',
      otherBrandUsed: currentAnswer?.otherBrandUsed || '',
    },
    {rules},
  );

  const selectedOptionData = questionnaire?.options?.find(i => i.value === inputs.selectedOption);

  const onSelectBrand = (value: {value: string; label: string} | null) => {
    setInputs({brandUsed: value?.value, brandLabel: value?.label});
    setTimeout(() => {
      window.scrollTo({top: 200, behavior: 'smooth'});
    }, 500);
  };

  const onClickSubmit = async () => {
    if (inputs.selectedOption === YES_OPTION) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: BIRTH_CONTROL_TELECONSULTATION_KEYS.BRAND_USED,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_TELECONSULTATION_KEYS.MEDICAL_HISTORY}));
  };

  const renderHappyRelation = (relation: Relation) => {
    if (inputs.selectedHappyOption === NO_OPTION) {
      const selectedHappyOptionData = relation?.options?.find(i => i.value === inputs.selectedHappyOption);
      return (
        <UiBox w="100%" mt={spacing.small}>
          {selectedHappyOptionData?.relations?.map(i => {
            if (i?.elementType?.name === 'inputArea') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.name}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="xl" fontWeight="600" mt={spacing.medium}>
                    {i?.questions?.default}
                  </UiText>
                  <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
                    <UiTextarea
                      mt={spacing.small}
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={i?.elementType?.placeholder}
                      value={inputs.medicationsTaking}
                      onChange={({target}) => setInputs({medicationsTaking: target.value})}
                      minH="200px"
                    />
                    {validation?.errors?.medicationsTaking && (
                      <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </UiBox>
              );
            }
            return null;
          })}
        </UiBox>
      );
    }
    return null;
  };

  const renderHappyQuestion = () => {
    if (inputs.selectedOption === YES_OPTION) {
      let data = {} as Relation;
      if (selectedOptionData?.relations?.length) {
        data = selectedOptionData?.relations[1];
      }
      if (!data.elementType || !data.options.length) return null;
      return (
        <UiBox w="100%" mt={spacing.medium} className="animate__animated animate__fadeInLeft">
          <UiText fontSize="md" fontWeight="bold">
            Were you happy with this option?
          </UiText>
          <UiSimpleGrid columns={2} spacing={5} mt={spacing.medium}>
            {data?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedHappyOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedHappyOption: value.value, medicationsTaking: ''})}
              />
            ))}
          </UiSimpleGrid>
          {renderHappyRelation(data)}
        </UiBox>
      );
    }
    return null;
  };

  const renderBrandUsed = () => {
    if (inputs.brandUsed === OTHER_BRAND && inputs.selectedOption === YES_OPTION) {
      return (
        <UiBox mt={spacing.small} w="100%" className="animate__animated animate__fadeInLeft">
          <UiText fontWeight="bold" fontSize="md">
            Tell us the brand/method you used
          </UiText>
          <UiFormControl id="otherBrandUsed" isRequired isInvalid={!!validation?.errors?.otherBrandUsed}>
            <UiTextarea
              mt={spacing.small}
              bg="white"
              borderRadius={spacing.tiny}
              placeholder="Type your response here..."
              maxLength={100}
              value={inputs.otherBrandUsed}
              onChange={({target}) => setInputs({otherBrandUsed: target.value})}
              minH="50px"
            />
            {validation?.errors?.otherBrandUsed && (
              <UiFormErrorMessage>{validation?.errors?.otherBrandUsed[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiBox>
      );
    }
    return null;
  };

  const renderDesc = () => {
    if (inputs.selectedOption === 'YES') {
      let data = {} as Relation;
      if (selectedOptionData?.relations?.length) {
        data = selectedOptionData?.relations[0];
      }
      if (!data.elementType || !data.options) return null;
      return (
        <UiBox w="100%" mt={spacing.medium} className="animate__animated animate__fadeInLeft">
          <UiText fontSize="md" fontWeight="bold">
            {data?.questions?.default}
          </UiText>
          <UiBox mt={spacing.small}>
            <CustomDropdown
              options={data.options}
              onChange={onSelectBrand}
              value={
                inputs.brandUsed
                  ? {
                      label: inputs.brandLabel,
                      value: inputs.brandUsed,
                    }
                  : undefined
              }
              defaultItem={{
                label: selectedBrand,
                value: inputs.brandUsed,
              }}
              icon={RiSearchLine}
              styleControl={{
                background: 'white',
                borderRadius: '3xl',
                height: '50px',
                paddingRight: '10px',
                paddingLeft: '10px',
              }}
              placeholder="Search for birth control brand..."
            />
          </UiBox>
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      disableNext={
        !inputs.selectedOption ||
        ((!inputs.brandUsed || !inputs.selectedHappyOption) && inputs.selectedOption === 'YES')
      }
      checkoutStep={questionnaire?.checkoutStep}
      minH={inputs.selectedOption === 'YES' ? 900 : '100vh'}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />

        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderDesc()}
        {renderBrandUsed()}
        {renderHappyQuestion()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire4Screen;
